<template>
  <div class="vp-overlay" @click.self="$emit('close')">
    <div class="vp-modal">
      <div class="vp-header">
        <h6 class="vp-title">
          <i class="mdi mdi-stethoscope me-2" :style="{color: urgent ? '#FF6B6B' : '#BFA164'}"></i>
          {{ urgent ? 'Emergency — pick a vet now' : 'Send to a vet' }}
        </h6>
        <button class="btn btn-sm btn-outline-secondary" @click="$emit('close')"><i class="mdi mdi-close"></i></button>
      </div>

      <div v-if="urgent" class="vp-urgent-banner">
        <i class="mdi mdi-alert-octagon" style="font-size:18px;color:#FF6B6B;"></i>
        <span>Life-threatening signs flagged. The vet will be notified by app, email and SMS.</span>
      </div>

      <!-- Tabs -->
      <div class="vp-tabs">
        <button class="vp-tab" :class="{'vp-tab-active': tab==='mine'}" @click="tab='mine'">
          <i class="mdi mdi-account-heart me-1"></i>My Vets
          <span v-if="priorVets.length" class="vp-pill">{{ priorVets.length }}</span>
        </button>
        <button class="vp-tab" :class="{'vp-tab-active': tab==='find'}" @click="tab='find'">
          <i class="mdi mdi-magnify me-1"></i>Find a Vet
          <span v-if="nearbyVets.length" class="vp-pill">{{ nearbyVets.length }}</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="vp-loading">
        <div class="spinner-border spinner-border-sm me-2"></div>Loading vets...
      </div>

      <!-- Empty state -->
      <div v-else-if="tab==='mine' && !priorVets.length" class="vp-empty">
        <i class="mdi mdi-account-search" style="font-size:36px;color:#6B6356;display:block;margin-bottom:6px;"></i>
        <div style="font-size:13px;color:#A09888;">You haven't messaged a vet before.</div>
        <button class="btn btn-sm btn-warning mt-3" @click="tab='find'">
          <i class="mdi mdi-magnify me-1"></i>Find a Vet
        </button>
      </div>
      <div v-else-if="tab==='find' && !nearbyVets.length" class="vp-empty">
        <i class="mdi mdi-stethoscope-off" style="font-size:36px;color:#6B6356;display:block;margin-bottom:6px;"></i>
        <div style="font-size:13px;color:#A09888;">No vets available right now.</div>
        <a class="btn btn-sm btn-outline-light mt-3" href="tel:+254800720555">
          <i class="mdi mdi-phone me-1"></i>Call KVB Hotline
        </a>
      </div>

      <!-- Vet list -->
      <div v-else class="vp-list">
        <div v-for="v in (tab==='mine' ? priorVets : nearbyVets)" :key="v.user_id"
             class="vp-card" :class="{'vp-card-active': selectedVetId===v.user_id}"
             @click="selectedVetId = v.user_id">
          <div class="vp-avatar">
            <img v-if="v.avatar_url" :src="avatarSrc(v.avatar_url)" />
            <i v-else class="mdi mdi-account"></i>
          </div>
          <div class="vp-card-body">
            <div class="vp-card-name">
              {{ v.vet_name || 'Vet' }}
              <span v-if="v.offers_emergency==1" class="vp-badge vp-badge-em">24/7</span>
              <span v-if="v.offers_telemedicine==1" class="vp-badge vp-badge-tm">Tele</span>
            </div>
            <div v-if="v.clinic_name" class="vp-card-sub">{{ v.clinic_name }}</div>
            <div v-if="v.specializations" class="vp-card-spec">{{ formatSpecs(v.specializations) }}</div>
            <div class="vp-card-meta">
              <span v-if="v.avg_rating"><i class="mdi mdi-star" style="color:#FFC107;"></i> {{ Number(v.avg_rating).toFixed(1) }}</span>
              <span v-if="v.distance_km"><i class="mdi mdi-map-marker"></i> {{ Number(v.distance_km).toFixed(1) }} km</span>
              <span v-if="v.phone"><i class="mdi mdi-phone"></i> {{ v.phone }}</span>
            </div>
            <!-- Existing chat strip: only on prior-vet cards -->
            <div v-if="v.conversation_id" class="vp-prior-chat" @click.stop="openChatThread(v)">
              <i class="mdi mdi-chat-processing-outline me-1" style="color:#4FC3F7;"></i>
              <span class="vp-prior-chat-text">
                <span v-if="v.last_message_preview" style="opacity:.85;">{{ v.last_message_preview.length > 60 ? v.last_message_preview.slice(0,60) + '…' : v.last_message_preview }}</span>
                <span v-else>You have an existing chat with this vet</span>
              </span>
              <i class="mdi mdi-arrow-right-thin ms-1"></i>
            </div>
          </div>
          <div v-if="selectedVetId===v.user_id" class="vp-check"><i class="mdi mdi-check-circle"></i></div>
        </div>
      </div>

      <!-- AI summary preview -->
      <div v-if="aiSummary" class="vp-ai-preview">
        <div class="vp-ai-label"><i class="mdi mdi-robot me-1"></i>AI summary that will be sent</div>
        <div class="vp-ai-body">{{ truncatedSummary }}</div>
      </div>

      <!-- Optional farmer note -->
      <div class="vp-note">
        <label class="vp-note-label">Add a note (optional)</label>
        <textarea v-model="note" rows="2" class="vp-note-input"
                  placeholder="e.g., She's still down. Can you come today?"></textarea>
      </div>

      <!-- Action -->
      <button class="vp-send-btn" :class="{'vp-send-urgent': urgent}"
              :disabled="!selectedVetId || sending" @click="send">
        <span v-if="sending"><span class="spinner-border spinner-border-sm me-1"></span>Sending...</span>
        <span v-else><i class="mdi mdi-send me-1"></i>{{ urgent ? 'Notify vet NOW' : 'Send to vet' }}</span>
      </button>
      <div class="vp-footer-hint">
        <i class="mdi mdi-information-outline me-1"></i>Vet receives app message + email + SMS.
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, flocker } from "@/api";

export default {
  name: "VetPickerModal",
  props: {
    aiSummary: { type: String, default: '' },
    urgent: { type: Boolean, default: false },
    animalId: { type: String, default: '' },
    species: { type: String, default: '' },
    consultationId: { type: String, default: '' },
    defaultMode: { type: String, default: 'mine' }
  },
  data(){return{
    tab: this.defaultMode === 'find' ? 'find' : 'mine',
    loading: true,
    sending: false,
    priorVets: [],
    nearbyVets: [],
    selectedVetId: '',
    note: ''
  }},
  computed: {
    truncatedSummary(){
      if (!this.aiSummary) return '';
      return this.aiSummary.length > 280 ? this.aiSummary.slice(0, 280) + '…' : this.aiSummary;
    }
  },
  mounted(){
    var self = this;
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        function(p){ self.fetchVets(p.coords.latitude, p.coords.longitude); },
        function(){ self.fetchVets('', ''); },
        { timeout: 4000, maximumAge: 60000 }
      );
    } else {
      this.fetchVets('', '');
    }
  },
  methods: {
    async fetchVets(lat, lng){
      this.loading = true;
      try {
        var r = await executeJson({
          function: 'getMyVets',
          userId: sessionStorage.getItem('userId') || '',
          latitude: lat || '',
          longitude: lng || ''
        }, flocker);
        if (r.data && r.data.success && r.data.data) {
          this.priorVets = r.data.data.priorVets || [];
          this.nearbyVets = r.data.data.nearbyVets || [];
          // If no prior vets, jump straight to the find tab
          if (!this.priorVets.length) this.tab = 'find';
          // Auto-select first vet for one-tap urgent send
          if (this.urgent) {
            var first = (this.priorVets[0] || this.nearbyVets[0]);
            if (first) this.selectedVetId = first.user_id;
          }
        }
      } catch(e){ /* noop — empty state will show */ }
      this.loading = false;
    },
    openChatThread(v){
      this.$emit('close');
      this.$router.push('/messages/' + v.conversation_id);
    },
    formatSpecs(s){
      if (!s) return '';
      if (Array.isArray(s)) return s.join(' · ').replace(/_/g,' ');
      try {
        var arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.join(' · ').replace(/_/g,' ');
      } catch(e) { /* fall through to plain-string formatting */ }
      return String(s).replace(/[[\]"]/g,'').replace(/_/g,' ').replace(/,/g,' · ');
    },
    avatarSrc(url){
      if (!url) return '';
      if (/^https?:/i.test(url)) return url;
      return flocker + (url.charAt(0) === '/' ? url.slice(1) : url);
    },
    async send(){
      if (!this.selectedVetId || this.sending) return;
      this.sending = true;
      try {
        var r = await executeJson({
          function: 'requestVetConsultation',
          userId: sessionStorage.getItem('userId') || '',
          vetUserId: this.selectedVetId,
          aiSummary: this.aiSummary || '',
          note: this.note || '',
          urgency: this.urgent ? 1 : 0,
          animalId: this.animalId || '',
          species: this.species || '',
          consultationId: this.consultationId || ''
        }, flocker);
        if (r.data && r.data.success) {
          var d = r.data.data || {};
          this.$emit('sent', {
            conversationId: d.conversationId,
            vetName: d.vetName,
            emailSent: d.emailSent,
            smsSent: d.smsSent
          });
        } else if (r.data && r.data.data && r.data.data.code === 'limit_reached') {
          var hrs = r.data.data.reset_in_hours || 24;
          alert("You've reached today's vet-request limit. Try again in " + hrs + " hour" + (hrs>1?'s':'') + ", or call your vet directly using the number on their card.");
        } else {
          alert((r.data && r.data.message) || 'Could not send to the vet. Please try again.');
        }
      } catch(e){
        alert('Connection error. Please try again.');
      }
      this.sending = false;
    }
  }
};
</script>

<style scoped>
.vp-overlay{position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:9999;display:flex;align-items:center;justify-content:center;padding:14px;}
.vp-modal{width:100%;max-width:520px;max-height:92vh;overflow-y:auto;background:#141414;border:1px solid #1E1E1E;border-radius:14px;padding:18px;display:flex;flex-direction:column;color:#F5F0E8;}
html[data-theme="light"] .vp-modal{background:#FFFFFF;border-color:#E8E2DA;color:#1A1A1A;}
.vp-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;}
.vp-title{margin:0;font-size:15px;font-weight:700;}
.vp-urgent-banner{display:flex;align-items:center;gap:8px;background:rgba(255,107,107,.08);border:1px solid #FF6B6B;border-radius:8px;padding:8px 10px;font-size:11px;color:#FF6B6B;margin-bottom:10px;}
.vp-tabs{display:flex;gap:6px;margin-bottom:10px;}
.vp-tab{flex:1;background:transparent;border:1px solid #2A2520;color:#A09888;padding:6px 8px;border-radius:8px;font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:4px;}
.vp-tab-active{background:#BFA164;color:#0A0A0A;border-color:#BFA164;font-weight:600;}
html[data-theme="light"] .vp-tab{border-color:#E0D8D0;color:#666;}
.vp-pill{background:rgba(0,0,0,.15);font-size:10px;padding:1px 6px;border-radius:8px;margin-left:4px;}
.vp-loading{padding:30px;text-align:center;color:#A09888;font-size:12px;}
.vp-empty{padding:30px 16px;text-align:center;}
.vp-list{flex:1;overflow-y:auto;max-height:340px;display:flex;flex-direction:column;gap:6px;margin-bottom:10px;padding-right:2px;}
.vp-card{display:flex;align-items:flex-start;gap:10px;background:#0F0F0F;border:1px solid #1E1E1E;border-radius:10px;padding:10px;cursor:pointer;transition:all .15s;}
.vp-card:hover{border-color:#BFA164;}
.vp-card-active{border-color:#BFA164;background:rgba(191,161,100,.08);}
html[data-theme="light"] .vp-card{background:#F7F5F2;border-color:#E0D8D0;}
html[data-theme="light"] .vp-card-active{background:#FFF7E6;border-color:#BFA164;}
.vp-avatar{width:36px;height:36px;border-radius:50%;background:#1E1E1E;display:flex;align-items:center;justify-content:center;flex-shrink:0;overflow:hidden;}
.vp-avatar img{width:100%;height:100%;object-fit:cover;}
.vp-avatar i{font-size:20px;color:#6B6356;}
.vp-card-body{flex:1;min-width:0;}
.vp-card-name{font-size:13px;font-weight:600;color:inherit;display:flex;flex-wrap:wrap;align-items:center;gap:4px;}
.vp-card-sub{font-size:11px;color:#BFA164;margin-top:2px;}
.vp-card-spec{font-size:10px;color:#A09888;margin-top:2px;}
html[data-theme="light"] .vp-card-spec{color:#777;}
.vp-card-meta{display:flex;flex-wrap:wrap;gap:8px;margin-top:4px;font-size:10px;color:#6B6356;}
html[data-theme="light"] .vp-card-meta{color:#888;}
.vp-card-meta span{display:inline-flex;align-items:center;gap:2px;}
.vp-badge{font-size:9px;padding:1px 6px;border-radius:4px;font-weight:600;}
.vp-badge-em{background:#FF6B6B;color:#fff;}
.vp-badge-tm{background:#4FC3F7;color:#0A0A0A;}
.vp-check{font-size:22px;color:#BFA164;}
.vp-prior-chat{display:flex;align-items:center;gap:4px;background:rgba(79,195,247,.08);border:1px solid rgba(79,195,247,.3);border-radius:6px;padding:5px 8px;margin-top:6px;font-size:11px;color:#4FC3F7;cursor:pointer;}
.vp-prior-chat:hover{background:rgba(79,195,247,.16);}
.vp-prior-chat-text{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.vp-ai-preview{background:rgba(206,147,216,.06);border:1px solid rgba(206,147,216,.4);border-radius:8px;padding:8px 10px;margin-bottom:10px;}
.vp-ai-label{font-size:10px;color:#CE93D8;font-weight:600;margin-bottom:4px;}
.vp-ai-body{font-size:11px;color:#A09888;line-height:1.5;max-height:100px;overflow-y:auto;white-space:pre-wrap;}
html[data-theme="light"] .vp-ai-body{color:#666;}
.vp-note{margin-bottom:10px;}
.vp-note-label{font-size:11px;color:#A09888;display:block;margin-bottom:4px;}
.vp-note-input{width:100%;background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;border-radius:8px;padding:8px 10px;font-size:12px;resize:vertical;}
html[data-theme="light"] .vp-note-input{background:#F7F5F2;border-color:#E0D8D0;color:#1A1A1A;}
.vp-send-btn{width:100%;background:#BFA164;color:#0A0A0A;border:none;padding:10px 16px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;}
.vp-send-btn:disabled{opacity:.4;cursor:not-allowed;}
.vp-send-urgent{background:#FF6B6B;color:#fff;}
.vp-footer-hint{font-size:10px;color:#6B6356;text-align:center;margin-top:6px;}
</style>
